import React, { useCallback } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAgencyAuth } from "../context/AgencyAuthContext";
import { ConnectionProvider } from "../context/ConnectionContext";
import ConnectionWrapper from "./ConnectionWrapper";
import Loading from "./common/Loading";
import { useSessionTimeout } from "../hooks/useSessionTimeout";
import SessionTimeoutWarning from "./SessionTimeoutWarning";

const AgencyProtectedRoute: React.FC = () => {
  const {
    isAuthenticated,
    loading,
    mfaRequired,
    mfaVerified,
    mfaConfigured,
    setIsAuthenticated,
    setAgency,
  } = useAgencyAuth();
  const navigate = useNavigate();

  const handleTimeout = useCallback(async () => {
    try {
      await axios.post("/api/auth/logout");
    } catch {}
    setIsAuthenticated(false);
    setAgency(null);
    navigate("/agency/login?reason=inactivity");
  }, [setIsAuthenticated, setAgency, navigate]);

  const { showWarning, secondsRemaining, extendSession } = useSessionTimeout({
    enabled: isAuthenticated && (!mfaRequired || mfaVerified),
    onTimeout: handleTimeout,
  });

  if (loading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/agency/login" replace />;
  }

  // MFA gate: redirect to setup or challenge if MFA is required but not verified
  if (mfaRequired && !mfaVerified) {
    if (mfaConfigured) {
      return <Navigate to="/agency/mfa-challenge" replace />;
    } else {
      return <Navigate to="/agency/mfa-setup" replace />;
    }
  }

  return (
    <ConnectionProvider>
      <ConnectionWrapper>
        <Outlet />
        {showWarning && (
          <SessionTimeoutWarning
            secondsRemaining={secondsRemaining}
            onContinue={extendSession}
            onLogout={handleTimeout}
          />
        )}
      </ConnectionWrapper>
    </ConnectionProvider>
  );
};

export default AgencyProtectedRoute;
